import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import DazzedHeading20 from 'components/common/text/DazzedHeading20'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import GrinRadioGroup from 'components/common/GrinRadioGroup'
import PrimaryButton from 'components/common/buttons/PrimaryButton'
import Actions from 'actions'
import usePlanLimits from 'hooks/usePlanLimits'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useRolePermissions from 'hooks/useRolePermissions'
import { trackEvent } from 'utils/analyticsUtils'

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: -60,
    marginBottom: 60
  },
  sectionHeader: {
    marginBottom: 5
  },
  optionLabel: {
    fontWeight: 'bold',
    fontSize: 14
  }
}))

const DefaultPatientTabSettings = () => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const isLoading = useSelector(state => state.profileReducer.isLoading)
  const appSettings = useSelector(state => state.profileReducer?.doctor?.user?.appSettings)

  const { isGP, isHI } = usePlanLimits()
  const { copilotControl: copilotFF } = useFeatureFlags()
  const { isHiUser } = useRolePermissions()

  const allowedOptions = useMemo(() => {
    if (isHiUser) {
      return ['chat']
    }
    if (isGP) {
      return ['notes', 'chat']
    }
    if (isHI && copilotFF) {
      return ['copilot', 'chat', 'notes']
    }
    return ['chat', 'notes']
  }, [isHiUser, isGP, isHI, copilotFF])

  const options = useMemo(
    () =>
      allowedOptions.map(value => ({
        value,
        label:
          value === 'copilot'
            ? t('pages.accountSettings.advancedActions.defaultPatientTab.options.copilot', 'Copilot')
            : value === 'notes'
            ? t('pages.accountSettings.advancedActions.defaultPatientTab.options.notes', 'Internal Notes')
            : t('pages.accountSettings.advancedActions.defaultPatientTab.options.chat', 'Chat')
      })),
    [allowedOptions, t]
  )

  const planDefault = useMemo(() => {
    if (isHiUser) {
      return 'chat'
    }
    if (isGP) {
      return 'notes'
    }
    if (isHI && copilotFF) {
      return 'copilot'
    }
    return 'chat'
  }, [isHiUser, isGP, isHI, copilotFF])

  const savedView = useMemo(() => {
    try {
      return JSON.parse(appSettings || '{}')?.defaultPatientView
    } catch (e) {
      return undefined
    }
  }, [appSettings])

  const [selectedOption, setSelectedOption] = useState(planDefault)

  useEffect(() => {
    if (savedView && allowedOptions.includes(savedView)) {
      setSelectedOption(savedView)
      return
    }
    setSelectedOption(planDefault)
  }, [savedView, allowedOptions, planDefault])

  useEffect(() => {
    trackEvent('Default patient tab - open')
  }, [])

  const handleOptionChange = useCallback(value => {
    setSelectedOption(value)
    trackEvent('Default patient tab - option selected', { value })
  }, [])

  const handleSave = useCallback(() => {
    if (!selectedOption) {
      return
    }
    trackEvent('Default patient tab - save', { value: selectedOption })
    dispatch({
      type: Actions.REQUEST_UPDATE_USER_APP_SETTINGS,
      payload: {
        defaultPatientView: selectedOption
      }
    })
  }, [dispatch, selectedOption])

  return (
    <Grid container direction="column" spacing={0} className={classes.root}>
      <Grid item className={classes.sectionHeader}>
        <DazzedHeading20>
          {t('pages.accountSettings.advancedActions.defaultPatientTab.title', 'Default Patient Tab')}
        </DazzedHeading20>
      </Grid>
      <Grid item>
        <DazzedParagraph14 strong>
          {t(
            'pages.accountSettings.advancedActions.defaultPatientTab.description',
            'Choose the default tab in the patient card that will be automatically selected when you open a patient.'
          )}
        </DazzedParagraph14>
      </Grid>
      <Grid item style={{ marginTop: 8 }}>
        <GrinRadioGroup
          options={options}
          value={selectedOption}
          setValue={handleOptionChange}
          labelClassName={classes.optionLabel}
          size="small"
          isDisabled={false}
          row={false}
        />
      </Grid>
      <Grid item style={{ marginTop: 12 }}>
        <PrimaryButton
          isLoading={isLoading}
          label={t('general.save')}
          disabled={!selectedOption}
          onClick={handleSave}
        />
      </Grid>
    </Grid>
  )
}

export default DefaultPatientTabSettings
